<div
  class="mb-4 rounded-md border border-gray-200 bg-white p-4"
  [formGroup]="companyForm"
>
  <h2 class="mb-4 text-lg text-primary-500">
    {{ "accountMenu.billingInformation.title" | transloco }}
  </h2>

  <!-- Sous-card: Informations entreprise -->
  <div class="mb-4 rounded-lg border border-greyscale-200 bg-primary-50 p-4">
    <div class="mb-3 flex items-center gap-2">
      <lucide-angular [img]="buildingIcon" class="h-4 w-4 text-primary-500" />
      <h3 class="text-sm font-semibold text-greyscale-700">
        {{ "accountMenu.billingInformation.companySection" | transloco }}
      </h3>
    </div>

    <!-- Greentripper Public ID -->
    <div class="mb-4 flex flex-col gap-1">
      <div class="text-sm font-semibold text-greyscale-600">
        {{ "accountMenu.billingInformation.publicIdLabel" | transloco }}
      </div>
      <div class="flex gap-2">
        <input
          type="text"
          [value]="company?.publicId ?? ''"
          readonly
          pInputText
          class="flex-1 font-mono text-paragraph-xs-reg"
          [fluid]="true"
          data-testid="company.publicId.input"
        />
        <p-button
          icon="pi pi-copy"
          severity="secondary"
          [pTooltip]="
            'accountMenu.billingInformation.publicIdCopyButton' | transloco
          "
          (onClick)="copyPublicId()"
          data-testid="company.publicId.copy"
        />
      </div>
    </div>

    <!-- Logo entreprise -->
    <div class="mb-4 flex flex-col gap-1">
      <div class="text-sm font-semibold text-greyscale-600">
        {{ "logoUpload.companyLogoLabel" | transloco }}
      </div>
      <app-logo-upload
        [currentLogoUrl]="company?.logoUrl"
        [uploading]="logoUploading"
        (logoCropped)="onLogoCropped($event)"
      />
    </div>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <!-- Nom de l'entreprise -->
      <div class="flex flex-col gap-1">
        <label for="name" class="text-sm font-semibold text-greyscale-600">
          {{ "accountMenu.billingInformation.companyName" | transloco }}
        </label>
        <input
          id="name"
          type="text"
          formControlName="name"
          [placeholder]="
            'accountMenu.billingInformation.placeholder.companyName' | transloco
          "
          pInputText
          data-testid="company.name.input"
        />
        @if (
          companyForm.get("name")?.touched && companyForm.get("name")?.invalid
        ) {
          <div class="text-xs text-red-500">
            {{
              "accountMenu.organizationsManagement.nameIsRequired" | transloco
            }}
          </div>
        }
      </div>

      <!-- Numéro de TVA -->
      <div class="flex flex-col gap-1">
        <label for="vatNumber" class="text-sm font-semibold text-greyscale-600">
          {{ "accountMenu.billingInformation.vatNumber" | transloco }}
        </label>
        <input
          id="vatNumber"
          type="text"
          formControlName="vatNumber"
          [placeholder]="
            'accountMenu.billingInformation.placeholder.vatNumber' | transloco
          "
          pInputText
          data-testid="company.vat.number.input"
        />
        @if (
          companyForm.get("vatNumber")?.touched &&
          companyForm.get("vatNumber")?.invalid
        ) {
          <div class="text-xs text-red-500">
            {{
              "accountMenu.organizationsManagement.vatIsRequired" | transloco
            }}
          </div>
        }
        @if (vatFormatWarning) {
          <div class="flex items-center gap-1 text-xs text-warning-600">
            <lucide-angular [img]="alertIcon" class="h-3 w-3" />
            <span>{{
              vatFormatWarning.key | transloco: vatFormatWarning.params
            }}</span>
          </div>
        }
      </div>
      <!-- <div class="flex flex-col gap-1">
        <label for="size" class="text-sm font-semibold text-greyscale-600">
          {{ "accountMenu.billingInformation.companySize" | transloco }}
        </label>
        <p-select
          id="size"
          formControlName="size"
          [placeholder]="
            'accountMenu.billingInformation.placeholder.companySize' | transloco
          "
          [options]="sizeOptions"
          class="w-full"
        />
        <app-error [control]="companyForm.get('size')!" />
      </div> -->
    </div>
  </div>

  <!-- Sous-card: Adresse de facturation -->
  <div class="mb-4 rounded-lg border border-greyscale-200 bg-primary-50 p-4">
    <div class="mb-3 flex items-center gap-2">
      <lucide-angular [img]="mapPinIcon" class="h-4 w-4 text-primary-500" />
      <h3 class="text-sm font-semibold text-greyscale-700">
        {{ "accountMenu.billingInformation.addressSection" | transloco }}
      </h3>
    </div>

    <!-- Rue + Numéro -->
    <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
      <div class="flex flex-col gap-1 md:col-span-3">
        <label for="street" class="text-sm font-semibold text-greyscale-600">
          {{ "accountMenu.billingInformation.street" | transloco }}
        </label>
        <input
          id="street"
          formControlName="street"
          [placeholder]="
            'accountMenu.billingInformation.placeholder.street' | transloco
          "
          pInputText
          data-testid="company.street.input"
        />
        @if (
          companyForm.get("street")?.touched &&
          companyForm.get("street")?.invalid
        ) {
          <div class="text-xs text-red-500">
            {{
              "accountMenu.organizationsManagement.streetIsRequired" | transloco
            }}
          </div>
        }
      </div>

      <div class="flex flex-col gap-1">
        <label
          for="streetNumber"
          class="text-sm font-semibold text-greyscale-600"
        >
          {{ "accountMenu.billingInformation.streetNumber" | transloco }}
        </label>
        <input
          id="streetNumber"
          formControlName="streetNumber"
          [placeholder]="
            'accountMenu.billingInformation.placeholder.streetNumber'
              | transloco
          "
          pInputText
          data-testid="company.street.number.input"
        />
        @if (
          companyForm.get("streetNumber")?.touched &&
          companyForm.get("streetNumber")?.invalid
        ) {
          <div class="text-xs text-red-500">
            {{
              "accountMenu.organizationsManagement.streetNumberIsRequired"
                | transloco
            }}
          </div>
        }
      </div>
    </div>

    <!-- Ville + Code postal + Pays -->
    <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
      <div class="flex flex-col gap-1">
        <label for="city" class="text-sm font-semibold text-greyscale-600">
          {{ "accountMenu.billingInformation.city" | transloco }}
        </label>
        <input
          id="city"
          formControlName="city"
          [placeholder]="
            'accountMenu.billingInformation.placeholder.city' | transloco
          "
          pInputText
          data-testid="company.city.input"
          [pTooltip]="
            !currentUserService.isAdmin()
              ? ('accountMenu.onlyAdminCanEdit' | transloco)
              : undefined
          "
        />
        @if (
          companyForm.get("city")?.touched && companyForm.get("city")?.invalid
        ) {
          <div class="text-xs text-red-500">
            {{
              "accountMenu.organizationsManagement.cityIsRequired" | transloco
            }}
          </div>
        }
      </div>

      <div class="flex flex-col gap-1">
        <label for="zipCode" class="text-sm font-semibold text-greyscale-600">
          {{ "accountMenu.billingInformation.zipCode" | transloco }}
        </label>
        <input
          id="zipCode"
          formControlName="zipCode"
          [placeholder]="
            'accountMenu.billingInformation.placeholder.zipCode' | transloco
          "
          pInputText
          data-testid="company.zip.code.input"
        />
        @if (
          companyForm.get("zipCode")?.touched &&
          companyForm.get("zipCode")?.invalid
        ) {
          <div class="text-xs text-red-500">
            {{
              "accountMenu.organizationsManagement.zipCodeIsRequired"
                | transloco
            }}
          </div>
        }
      </div>

      <div class="flex flex-col gap-1">
        <label for="country" class="text-sm font-semibold text-greyscale-600">
          {{ "accountMenu.billingInformation.country" | transloco }}
        </label>
        <input
          id="country"
          formControlName="country"
          [placeholder]="
            'accountMenu.billingInformation.placeholder.country' | transloco
          "
          pInputText
          data-testid="company.country.input"
        />
        @if (
          companyForm.get("country")?.touched &&
          companyForm.get("country")?.invalid
        ) {
          <div class="text-xs text-red-500">
            {{
              "accountMenu.organizationsManagement.countryIsRequired"
                | transloco
            }}
          </div>
        }
      </div>
    </div>
  </div>

  <!-- Bouton -->
  <div>
    <p-button
      type="button"
      (click)="onSubmit()"
      [disabled]="companyForm.invalid || !currentUserService.isAdmin()"
      data-testid="company.update.button"
      [pTooltip]="
        currentUserService.isAdmin()
          ? undefined
          : ('accountMenu.onlyAdminCanEdit' | transloco)
      "
      [loading]="isLoading"
    >
      <lucide-angular [img]="buildingIcon" class="mr-2 h-4 w-4" />
      <span class="font-semibold">{{
        "accountMenu.updateCompanyInfo" | transloco
      }}</span>
    </p-button>
  </div>
</div>
